<script setup lang="ts">
import type { CellCoord, CellErrorMsg } from "@/types"
import selectBanner from "@/assets/pngs/selectBanner.png"
import ImportTarget from "@/components/FileImport/ImportTarget.vue"
import LocateDataset from "@/components/FileImport/Locate/LocateDataset.vue"
import LocateTable from "@/components/FileImport/Locate/LocateTable.vue"
import Loading from "@/components/Loading.vue"
import PageBanner, { PageBannerType } from "@/components/PageBanner.vue"
import { useFileImportStore, useSettingsStore } from "@/stores"
import {
  ALPHA_APP_TOOLTIP,
  ALPHA_QUANTITY_SOLD,
  ALPHA_VARIANT_ID,
} from "@/utils/alphaModel"
import { KSpotlight } from "@kaleidoscope-solutions/ks-design-system"
import { storeToRefs } from "pinia"
import { computed, nextTick, onMounted, ref, watch } from "vue"

const fileImportStore = useFileImportStore()
const settingsStore = useSettingsStore()

const { blockLoading } = storeToRefs(fileImportStore)

const scrapedFile = computed(() => fileImportStore.file?.scraped)
const alphabetHeaders = computed(() => scrapedFile.value?.sheetHeaders ?? [])

const selectedTableHeader = ref<number>()

const locateItems = computed<{ id: number; label: string }[]>(() =>
  (scrapedFile.value?.headers ?? []).map((v, k) => ({
    id: k,
    label: `${alphabetHeaders.value[k]} - ${v}`,
  }))
)

const showNonNumericBanner = ref(true)

function setSelectedTableHeader(value: number) {
  selectedTableHeader.value = value
}

function clearSelectedTableHeader() {
  selectedTableHeader.value = undefined
}

const keyTypes = computed(() => {
  return fileImportStore.targetKeyTypes ?? []
})

const invalidCells = computed((): Map<CellCoord, CellErrorMsg> => {
  return fileImportStore.invalidValuesInTargetColumn
})

const displayNonNumericBanner = computed(() => {
  return (
    fileImportStore.invalidValuesInTargetColumn.size > 0 &&
    showNonNumericBanner.value
  )
})

onMounted(() => {
  if (fileImportStore.targets?.includes(ALPHA_QUANTITY_SOLD)) {
    fileImportStore.locateTooltips = ALPHA_APP_TOOLTIP
  }

  nextTick(() => {
    // Select header for the first selected target.
    selectedTableHeader.value = fileImportStore.valueColumns?.[0]
  })
})

watch(
  () => fileImportStore.invalidValuesInTargetColumn,
  () => {
    showNonNumericBanner.value = true
  }
)

const showMixedContentBanner = ref(true)
const selectedColumnContainsMixedContent = computed(() => {
  if (selectedTableHeader.value === undefined) {
    return false
  }

  return (
    fileImportStore.dateColumnsWithMixedContent?.[selectedTableHeader.value] ??
    false
  )
})

const anySelectedColumnContainsMixedContent = computed(() => {
  return Array.from(fileImportStore.locateInfoInFile.entries()).some(
    ([_, column]) => {
      return fileImportStore.dateColumnsWithMixedContent?.[column] ?? false
    }
  )
})

const displayMixedBanner = computed(() => {
  return (
    anySelectedColumnContainsMixedContent.value && showMixedContentBanner.value
  )
})

function closeMixedContentBanner() {
  showMixedContentBanner.value = false
}

function bannerVisibility(openStatus: boolean) {
  settingsStore.showSelectMessage = openStatus
}
</script>

<template>
  <div class="flex h-full flex-1 flex-col gap-6 overflow-auto">
    <div class="flex flex-col gap-4 sticky left-0">
      <PageBanner
        v-if="displayNonNumericBanner"
        :type="PageBannerType.WARNING"
        label="Important"
        title="Non-numeric values found"
        class="pb-6"
        @close="showNonNumericBanner = false"
      >
        <p>
          Some values within <strong>{{ (fileImportStore.targets ?? []).join(', ') }}</strong> are non-numeric. The
          rows containing these values will be ignored.
        </p>
      </PageBanner>

      <PageBanner
        v-if="displayMixedBanner"
        :type="PageBannerType.WARNING"
        label="Important"
        title="Mixed date formats detected"
        class="pb-6"
        @close="closeMixedContentBanner"
      >
        <p>
          Content within '{{ fileImportStore.timeCategory }}' has mixed formats,
          please ensure the formatting of the cells in this column is consistent.
        </p>
      </PageBanner>

      <KSpotlight
        title="Select columns to import"
        :default-open="settingsStore.showSelectMessage"
        @spotlight-open="bannerVisibility"
      >
        <template #default>
          <div class="grid grid-cols-1 grid-rows-2 gap-4 lg:grid-cols-5 lg:grid-rows-1">
            <div class="flex flex-col gap-2 lg:col-span-2">
              <div class="text-pretty pb-2 pr-8 text-sm lg:pb-0">
                Note: <strong>{{ ALPHA_VARIANT_ID }}</strong> often has a different
                column name depending on the source e.g. “product-name” in Amazon,
                or “Item title” in Ebay.
              </div>
            </div>

            <div class="-mb-4 grid place-content-end pr-8 lg:col-span-3">
              <img
                :src="selectBanner"
                class="aspect-auto max-h-32"
                alt="image showing select boxes are linked to columns in file"
              >
            </div>
          </div>
        </template>
      </KSpotlight>
      <div class="flex">
        <div class="w-1/6 rounded-lg bg-gray-100 p-4">
          <ImportTarget />
        </div>
        <div
          class="flex flex-1 flex-wrap items-end gap-4 rounded-lg bg-gray-100 p-4"
        >
          <Loading
            v-if="blockLoading"
            class="h-40 w-40 m-auto"
          />
          <LocateDataset
            v-else-if="fileImportStore.isDatasetTarget"
            :locate-items="locateItems"
            :selected-table-header="selectedTableHeader"
            :key-types="keyTypes"
            @clear-selected-table-header="clearSelectedTableHeader"
            @update:selected-table-header="setSelectedTableHeader"
          />
        </div>
      </div>
    </div>

    <div class="h-full w-full flex-1">
      <!-- TODO: this is potentially undefined -->
      <LocateTable
        v-if="fileImportStore.file?.scraped"
        :table="fileImportStore.file?.scraped"
        :selected-header="selectedTableHeader"
        :invalid-cells="invalidCells"
        :selected-column-contains-mixed-content="
          selectedColumnContainsMixedContent
        "
      />
    </div>
  </div>
</template>
